/**
 * What a ticket status means.
 *
 * A hand port of `Contracts/TicketStatusRules.cs`, and the second of the two files in this app that
 * duplicate C# logic rather than generating it (`recurrence.ts` is the other) - `ContractsGen`
 * carries data and not behaviour. The duplication has a cost and the C# file names it: a rule
 * enforced in one place and worded differently in another is how "why won't this invoice?" becomes a
 * support call. `rules.test.ts` asserts this file against the generated enum, which is what stops a
 * status appended on the server from quietly falling out of the picker here.
 */
import { TicketStatus } from './contracts.gen.js';

/**
 * Every status, in WORKFLOW order - what a picker lists.
 *
 * Deliberately hand-ordered rather than derived from the enum: the enum is in declaration order,
 * which is stuck with the numeric order the appended values were forced into, so `Scheduled` (8)
 * would sit after `Closed` (4).
 */
export const ALL_TICKET_STATUSES: readonly TicketStatus[] = [
  TicketStatus.Open,
  TicketStatus.Scheduled,
  TicketStatus.InProgress,
  TicketStatus.Waiting,
  TicketStatus.WaitingForCustomer,
  TicketStatus.WaitingForParts,
  TicketStatus.WaitingForVendor,
  TicketStatus.OnHold,
  TicketStatus.Resolved,
  TicketStatus.Closed
];

/** How the status reads to a person. The enum name is the fallback, never the label. */
export function statusLabel(status: TicketStatus): string {
  switch (status) {
    case TicketStatus.Open:
      return 'Open';
    case TicketStatus.Scheduled:
      return 'Scheduled';
    case TicketStatus.InProgress:
      return 'In progress';
    case TicketStatus.Waiting:
      return 'Waiting';
    case TicketStatus.WaitingForCustomer:
      return 'Waiting for customer';
    case TicketStatus.WaitingForParts:
      return 'Waiting for parts';
    case TicketStatus.WaitingForVendor:
      return 'Waiting for vendor';
    case TicketStatus.OnHold:
      return 'On hold';
    case TicketStatus.Resolved:
      return 'Resolved';
    case TicketStatus.Closed:
      return 'Closed';
    default:
      return String(status);
  }
}

/**
 * The work is done.
 *
 * Everything else is live work - including every "waiting for" state, which is a ticket that is
 * still ours even though nobody can touch it today.
 */
export function isFinished(status: TicketStatus): boolean {
  return status === TicketStatus.Resolved || status === TicketStatus.Closed;
}

/** Live work: the complement of `isFinished`. */
export function isOpen(status: TicketStatus): boolean {
  return !isFinished(status);
}

/**
 * Blocked on something outside the technician's hands.
 *
 * A reporting bucket, not a rule: a blocked ticket bills exactly like any other unfinished one,
 * which is to say not at all.
 */
export function isWaiting(status: TicketStatus): boolean {
  return (
    status === TicketStatus.Waiting ||
    status === TicketStatus.WaitingForCustomer ||
    status === TicketStatus.WaitingForParts ||
    status === TicketStatus.WaitingForVendor ||
    status === TicketStatus.OnHold
  );
}

/**
 * Whether this ticket's labor and parts may go on an invoice.
 *
 * CLOSED COUNTS AS WELL AS RESOLVED, deliberately. Closed is strictly past Resolved, so reading
 * "only resolved" literally would strand the work on every ticket a technician tidied up before
 * billing ran - permanently, since the only way back would be to re-open a ticket that is genuinely
 * finished. Everything short of finished is refused, which is the rule that was actually asked for.
 */
export function isBillable(status: TicketStatus): boolean {
  return isFinished(status);
}

/** The rule in one sentence, so every surface that explains it says the same thing. */
export const BILLING_RULE =
  'Time and charges are invoiced per ticket, and only once the ticket is resolved.';
